import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';

const SLIDES = [
  { headline: 'welcome_headline_1', sub: 'welcome_sub_1', icon: 'building' },
  { headline: 'welcome_headline_2', sub: 'welcome_sub_2', icon: 'share' },
  { headline: 'welcome_headline_3', sub: 'welcome_sub_3', icon: 'find' },
  { headline: 'welcome_headline_4', sub: 'welcome_sub_4', icon: 'notify' },
] as const;

function SlideIllustration({ icon }: { icon: string }) {
  const size = 200;
  const common = { width: size, height: size, viewBox: '0 0 200 200', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' } as const;

  if (icon === 'building') {
    return (
      <svg {...common}>
        {/* Ground */}
        <rect x="20" y="160" width="160" height="20" rx="4" fill="#d8e2ff" />
        {/* Building */}
        <rect x="50" y="40" width="100" height="120" rx="6" fill="#005bc0" />
        <rect x="55" y="40" width="90" height="120" rx="4" fill="#0068d6" />
        {/* Windows */}
        {[0, 1, 2, 3].map(row =>
          [0, 1, 2].map(col => (
            <rect key={`${row}-${col}`} x={65 + col * 28} y={52 + row * 26} width="18" height="14" rx="2" fill={row === 3 && col === 1 ? '#ffd166' : '#d8e2ff'} opacity={row === 3 && col === 1 ? 1 : 0.7} />
          ))
        )}
        {/* Parking area */}
        <rect x="30" y="148" width="140" height="14" rx="3" fill="#e8edf5" />
        {/* Cars */}
        <rect x="38" y="151" width="24" height="8" rx="4" fill="#8ba4c9" />
        <rect x="70" y="151" width="24" height="8" rx="4" fill="#8ba4c9" />
        {/* Free spot glow */}
        <rect x="102" y="149" width="28" height="12" rx="4" fill="#4ade80" opacity="0.35" />
        <rect x="105" y="151" width="22" height="8" rx="4" fill="#4ade80" opacity="0.6" />
        {/* Another car */}
        <rect x="138" y="151" width="24" height="8" rx="4" fill="#8ba4c9" />
      </svg>
    );
  }

  if (icon === 'share') {
    return (
      <svg {...common}>
        {/* Calendar background */}
        <rect x="35" y="30" width="130" height="120" rx="12" fill="#d8e2ff" />
        <rect x="35" y="30" width="130" height="30" rx="12" fill="#005bc0" />
        <rect x="35" y="48" width="130" height="14" fill="#005bc0" />
        {/* Calendar dots */}
        {[0, 1, 2, 3].map(col =>
          [0, 1, 2].map(row => {
            const isActive = (col === 1 && row === 0) || (col === 2 && row === 0) || (col === 1 && row === 1);
            return (
              <circle key={`${row}-${col}`} cx={62 + col * 30} cy={82 + row * 24} r="8" fill={isActive ? '#4ade80' : '#f0f2f5'} />
            );
          })
        )}
        {/* Checkmarks on green days */}
        <path d="M54 80 l4 4 l8 -8" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        {/* Clock icon */}
        <circle cx="150" cy="145" r="24" fill="#005bc0" />
        <circle cx="150" cy="145" r="20" fill="white" />
        <line x1="150" y1="145" x2="150" y2="133" stroke="#005bc0" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="150" y1="145" x2="160" y2="145" stroke="#005bc0" strokeWidth="2.5" strokeLinecap="round" />
        {/* Green check badge */}
        <circle cx="165" cy="130" r="10" fill="#4ade80" />
        <path d="M160 130 l3 3 l6 -6" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (icon === 'find') {
    return (
      <svg {...common}>
        {/* Phone outline */}
        <rect x="50" y="20" width="100" height="165" rx="16" fill="#e8edf5" />
        <rect x="55" y="35" width="90" height="135" rx="4" fill="white" />
        {/* Status bar */}
        <rect x="80" y="26" width="40" height="4" rx="2" fill="#c4cdd5" />
        {/* Spot cards */}
        {[0, 1, 2].map((i) => {
          const y = 44 + i * 40;
          const isFree = i === 1;
          return (
            <g key={i}>
              <rect x="62" y={y} width="76" height="32" rx="6" fill={isFree ? '#e8faf0' : '#f5f6f8'} stroke={isFree ? '#4ade80' : '#e0e3e8'} strokeWidth="1.5" />
              {/* Level badge */}
              <rect x="67" y={y + 6} width="16" height="16" rx="4" fill={isFree ? '#4ade80' : '#d8e2ff'} />
              <text x="75" y={y + 18} textAnchor="middle" fontSize="9" fontWeight="700" fill={isFree ? 'white' : '#005bc0'}>B{i + 1}</text>
              {/* Spot label */}
              <rect x="88" y={y + 8} width="30" height="5" rx="2" fill={isFree ? '#4ade80' : '#d0d4db'} opacity="0.6" />
              <rect x="88" y={y + 17} width="20" height="4" rx="2" fill={isFree ? '#4ade80' : '#d0d4db'} opacity="0.3" />
              {/* Status */}
              <circle cx="128" cy={y + 16} r="5" fill={isFree ? '#4ade80' : '#d0d4db'} />
            </g>
          );
        })}
        {/* Tap hand */}
        <circle cx="100" cy="102" r="18" fill="#005bc0" opacity="0.12" />
        <circle cx="100" cy="102" r="10" fill="#005bc0" opacity="0.2" />
      </svg>
    );
  }

  // notify
  return (
    <svg {...common}>
      {/* Bell body */}
      <path d="M100 30 C75 30 60 55 60 85 L60 110 L45 125 L155 125 L140 110 L140 85 C140 55 125 30 100 30Z" fill="#005bc0" />
      <circle cx="100" cy="135" r="12" fill="#005bc0" />
      <ellipse cx="100" cy="28" rx="6" ry="5" fill="#005bc0" />
      {/* Inner highlight */}
      <path d="M100 38 C80 38 68 58 68 84 L68 108 L55 120 L145 120 L132 108 L132 84 C132 58 120 38 100 38Z" fill="#3388e0" />
      {/* Notification bubble */}
      <rect x="105" y="55" width="70" height="40" rx="10" fill="white" />
      <rect x="105" y="80" width="12" height="12" rx="2" fill="white" transform="rotate(-20 105 80)" />
      {/* Bubble text lines */}
      <rect x="115" y="64" width="40" height="5" rx="2" fill="#d8e2ff" />
      <rect x="115" y="74" width="28" height="5" rx="2" fill="#4ade80" />
      {/* Pulse rings */}
      <circle cx="100" cy="85" r="55" stroke="#005bc0" strokeWidth="1.5" fill="none" opacity="0.15" />
      <circle cx="100" cy="85" r="70" stroke="#005bc0" strokeWidth="1" fill="none" opacity="0.08" />
    </svg>
  );
}

export default function WelcomePage() {
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();
  const { t, direction } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const isDragging = useRef(false);

  const isLast = current === SLIDES.length - 1;

  const finish = useCallback(() => {
    localStorage.setItem('onboarding_seen', '1');
    navigate('/login', { replace: true });
  }, [navigate]);

  const next = useCallback(() => {
    if (isLast) { finish(); return; }
    setCurrent(c => Math.min(c + 1, SLIDES.length - 1));
  }, [isLast, finish]);

  const prev = useCallback(() => {
    setCurrent(c => Math.max(c - 1, 0));
  }, []);

  // Swipe support
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX;
    isDragging.current = true;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const diff = e.changedTouches[0].clientX - startXRef.current;
    const threshold = 50;
    const isRTL = direction === 'rtl';
    if (Math.abs(diff) > threshold) {
      if (isRTL ? diff > 0 : diff < 0) next();
      else prev();
    }
  }, [direction, next, prev]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') direction === 'rtl' ? prev() : next();
      if (e.key === 'ArrowLeft') direction === 'rtl' ? next() : prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [direction, next, prev]);

  const slide = SLIDES[current];

  return (
    <div
      dir={direction}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: 'var(--surface, #f8f9fa)',
        overflow: 'hidden',
        position: 'relative',
        fontFamily: "'Heebo', 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      {/* Background blobs — matching LoginPage style */}
      <div style={{
        position: 'fixed', zIndex: 0,
        top: '-10%', right: '-10%',
        width: '500px', height: '500px',
        background: 'rgba(0,91,192,0.05)',
        borderRadius: '50%', filter: 'blur(120px)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'fixed', zIndex: 0,
        bottom: '-10%', left: '-10%',
        width: '500px', height: '500px',
        background: 'rgba(95,92,120,0.05)',
        borderRadius: '50%', filter: 'blur(120px)',
        pointerEvents: 'none',
      }} />

      {/* Skip button */}
      {!isLast && (
        <button
          onClick={finish}
          style={{
            position: 'absolute',
            top: '1.25rem',
            [direction === 'rtl' ? 'left' : 'right']: '1.25rem',
            zIndex: 10,
            background: 'none',
            border: 'none',
            color: 'var(--on-surface-3, #6b7280)',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            padding: '0.5rem',
          }}
        >
          {t('welcome_skip')}
        </button>
      )}

      {/* Content area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '420px',
        padding: '3rem 1.5rem 1.5rem',
        zIndex: 1,
      }}>
        {/* Logo */}
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <h1 style={{
            fontFamily: "var(--manrope, 'Manrope', sans-serif)",
            fontSize: '2rem',
            fontWeight: 800,
            letterSpacing: '-0.04em',
            color: 'var(--primary, #005bc0)',
            margin: 0,
          }}>Lot'N'Go</h1>
        </div>

        {/* Illustration */}
        <div
          ref={trackRef}
          style={{
            width: '200px',
            height: '200px',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SlideIllustration icon={slide.icon} />
        </div>

        {/* Text */}
        <div style={{ textAlign: 'center', minHeight: '110px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <h2 style={{
            fontFamily: "var(--manrope, 'Manrope', sans-serif)",
            fontSize: '1.5rem',
            fontWeight: 700,
            color: 'var(--on-surface, #1a1c1e)',
            margin: '0 0 0.75rem',
          }}>
            {t(slide.headline)}
          </h2>
          <p style={{
            fontFamily: "var(--inter, 'Inter', sans-serif)",
            fontSize: '0.9375rem',
            lineHeight: 1.6,
            color: 'var(--on-surface-2, #5f5c78)',
            margin: 0,
            padding: '0 0.5rem',
          }}>
            {t(slide.sub)}
          </p>
        </div>

        {/* Dots */}
        <div style={{
          display: 'flex',
          direction: 'ltr',
          gap: '8px',
          marginTop: '2rem',
          marginBottom: '2rem',
        }}>
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              aria-label={`Slide ${i + 1}`}
              style={{
                width: i === current ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                background: i === current ? 'var(--primary, #005bc0)' : 'var(--surface-high, #d8e2ff)',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </div>

        {/* Watch tour link — last slide only */}
        {isLast && (
          <a
            href="https://youtube.com/shorts/apAekxLcbRA"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--primary, #005bc0)',
              textDecoration: 'none',
              marginBottom: '1rem',
            }}
          >
            <span>▶</span>
            {t('welcome_watch_tour')}
          </a>
        )}

        {/* CTA button */}
        <button
          onClick={next}
          style={{
            width: '100%',
            maxWidth: '320px',
            padding: '1rem',
            borderRadius: '12px',
            border: 'none',
            background: 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
            color: 'white',
            fontFamily: "var(--manrope, 'Manrope', sans-serif)",
            fontSize: '16px',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,91,192,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
          }}
        >
          {isLast ? t('welcome_get_started') : t('welcome_next')}
          <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
            {direction === 'rtl' ? 'arrow_back' : 'arrow_forward'}
          </span>
        </button>

        {/* Sign in link */}
        <div style={{
          marginTop: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          fontSize: '14px',
        }}>
          <span style={{ color: 'var(--on-surface-3, #6b7280)' }}>
            {t('welcome_already_member')}
          </span>
          <button
            onClick={() => {
              localStorage.setItem('onboarding_seen', '1');
              navigate('/login', { replace: true });
            }}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--primary, #005bc0)',
              fontWeight: 700,
              cursor: 'pointer',
              padding: 0,
              fontSize: '14px',
            }}
          >
            {t('welcome_sign_in')}
          </button>
        </div>
      </div>

      {/* Bottom gradient strip — matching LoginPage */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0,
        width: '100%', height: '4px',
        background: 'linear-gradient(to right, #005bc0, #d8e2ff, #005bc0)',
        pointerEvents: 'none',
        zIndex: 10,
      }} />
    </div>
  );
}
